import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Container,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiDownload, FiRefreshCw, FiFilter } from "react-icons/fi";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetLocationDepartmentReport } from "../../services/ReportServices";
import {
  getLocationOptionApi,
  SearchDepartmentByLocationApi,
} from "../../services/SearchService";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="danger">
          Something went wrong with this component. Please try again.
        </Alert>
      );
    }
    return this.props.children;
  }
}

const AssetLocationDepartmentReport = () => {
  // Date range options
  const dateRangeOptions = [
    { label: "All", value: "all" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "7_days" },
    { label: "Last 15 Days", value: "15_days" },
    { label: "Last 30 Days", value: "30_days" },
    { label: "Last Month", value: "last_month" },
    { label: "Last 3 Months", value: "3_months" },
    { label: "Custom", value: "custom" },
  ];

  // State for data
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdowns
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(
    dateRangeOptions[4]
  );
  const [locationOptions, setLocationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // State for filters
  const [param, setParam] = useState({
    page: 1,
    page_size: 10,
    location: "",
    department: "",
    date_range: "all",
    start_date: "",
    end_date: "",
  });

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "₹0";
    return `₹${value.toLocaleString("en-IN")}`;
  };

  // Initialize component
  useEffect(() => {
    // Load locations
    const loadLocations = async () => {
      try {
        const response = await getLocationOptionApi();
        const options = response.data.results.map((location) => ({
          label: location.name,
          value: location.id,
        }));
        setLocationOptions(options);
      } catch (error) {
        console.error("Failed to load locations:", error);
        toast.error("Failed to load locations");
      }
    };

    loadLocations();

    // Set initial date range
    const today = new Date();
    const start_date = formatDate(today.setDate(today.getDate() - 7));
    const end_date = formatDate(new Date());

    setParam((prev) => ({
      ...prev,
      start_date,
      end_date,
    }));
  }, []);

  // Load departments when location changes
  useEffect(() => {
    const loadDepartments = async () => {
      if (!selectedLocation) {
        setDepartmentOptions([]);
        setSelectedDepartment(null);
        return;
      }

      try {
        const response = await SearchDepartmentByLocationApi(
          selectedLocation.value
        );

        // Handle both possible response formats
        const departments = response.data.results || [];
        const options = departments.map((dept) => ({
          label: dept.label || dept.name || "Unknown Department",
          value: dept.value || dept.id || "",
        }));

        setDepartmentOptions(options);
      } catch (error) {
        console.error("Failed to load departments:", error);
        toast.error("Failed to load departments");
        setDepartmentOptions([]);
      }
    };

    loadDepartments();
  }, [selectedLocation]);

  // Handle date range selection
  useEffect(() => {
    if (selectedDateRange.value === "custom") return;

    const today = new Date();
    let startDate = "";
    let endDate = "";

    switch (selectedDateRange.value) {
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case "7_days":
        startDate = formatDate(new Date(today.setDate(today.getDate() - 7)));
        endDate = formatDate(new Date());
        break;
      case "15_days":
      case "30_days":
      case "3_months":
        const days =
          selectedDateRange.value === "15_days"
            ? 15
            : selectedDateRange.value === "30_days"
            ? 30
            : 90;
        startDate = formatDate(new Date(today.setDate(today.getDate() - days)));
        endDate = formatDate(new Date());
        break;
      case "last_month":
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        startDate = formatDate(firstDayLastMonth);
        endDate = formatDate(lastDayLastMonth);
        break;
      default:
        startDate = "";
        endDate = "";
    }

    setParam((prev) => ({
      ...prev,
      date_range: selectedDateRange.value,
      start_date: startDate,
      end_date: endDate,
      page: 1,
    }));
  }, [selectedDateRange]);

  // Fetch data when filters change
  useEffect(() => {
    const delay = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(delay);
  }, [param]);

  // Fetch report data
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate custom date range
      if (
        param.date_range === "custom" &&
        (!param.start_date || !param.end_date)
      ) {
        throw new Error(
          "Please select both start and end dates for custom range"
        );
      }

      if (param.date_range === "custom") {
        const start = new Date(param.start_date);
        const end = new Date(param.end_date);

        if (start > end) {
          throw new Error("Start date cannot be after end date");
        }
      }

      const response = await getAssetLocationDepartmentReport({
        ...param,
        export: false,
      });

      if (!response.data) {
        throw new Error("Invalid response format from server");
      }

      setReportData(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error("Error fetching report data:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch asset location department report";
      setError(errorMessage);
      toast.error(errorMessage);
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle location change
  const handleLocationChange = (selected) => {
    console.log("Selected location:", selected);
    setSelectedLocation(selected);
    setSelectedDepartment(null);
    setParam((prev) => ({
      ...prev,
      location: selected?.value || "",
      department: "",
      page: 1,
    }));
  };

  // Handle department change
  const handleDepartmentChange = (selected) => {
    console.log("Selected department:", selected);
    setSelectedDepartment(selected);
    setParam((prev) => ({
      ...prev,
      department: selected?.value || "",
      page: 1,
    }));
  };

  // Handle page change
  const handlePageChange = (selectedPage) => {
    setParam((prev) => ({
      ...prev,
      page: selectedPage.selected + 1,
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await getAssetLocationDepartmentReport({
        ...param,
        export: true,
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `asset_location_department_report_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container fluid>
      {/* Breadcrumb and header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap ">
        <div>
          <h2 className="text-primary mb-0">
            Asset Location Department Report
          </h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0 ">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">
              Asset Location Department
            </li>
          </ol>
        </div>

        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>
      <ErrorBoundary>
        <Card style={{ minHeight: "350px" }}>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0">
              <FiFilter className="me-2" /> Asset Distribution
            </h5>

            <div className="d-flex gap-2 flex-wrap align-items-center">
              <ErrorBoundary>
                <GlobalSearchableDropdown
                  options={locationOptions}
                  selected={selectedLocation}
                  onSelect={handleLocationChange}
                  placeholder="Location"
                  width="180px"
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <GlobalSearchableDropdown
                  options={departmentOptions}
                  selected={selectedDepartment}
                  onSelect={handleDepartmentChange}
                  placeholder="Department"
                  width="180px"
                  isDisabled={!selectedLocation}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <GlobalSearchableDropdown
                  options={dateRangeOptions}
                  selected={selectedDateRange}
                  onSelect={(selected) =>
                    setSelectedDateRange(selected || dateRangeOptions[0])
                  }
                  isClearable={false}
                  placeholder="Date Range"
                  width="150px"
                />
              </ErrorBoundary>

              {selectedDateRange.value === "custom" && (
                <div style={{ width: "260px" }}>
                  <DatePicker
                    selectsRange
                    startDate={
                      param.start_date ? new Date(param.start_date) : null
                    }
                    endDate={param.end_date ? new Date(param.end_date) : null}
                    onChange={([start, end]) => {
                      setParam((prev) => ({
                        ...prev,
                        date_range: "custom",
                        start_date: start ? formatDate(start) : "",
                        end_date: end ? formatDate(end) : "",
                        page: 1,
                      }));
                    }}
                    isClearable
                    placeholderText="Select date range"
                    className="form-control form-control-sm"
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                  />
                </div>
              )}
            </div>
          </Card.Header>

          <Card.Body className="p-0">
            {error && (
              <Alert variant="danger">
                {error}
                <Button variant="link" size="sm" onClick={fetchData}>
                  <FiRefreshCw /> Try again
                </Button>
              </Alert>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading asset distribution report...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle text-nowrap small">
                    <thead className="table-light">
                      <tr>
                        <th>Location</th>
                        <th>Department</th>
                        <th>Category</th>
                        <th>Asset Count</th>
                        <th>Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            <small>
                              No asset distribution found matching your criteria
                            </small>
                          </td>
                        </tr>
                      ) : (
                        reportData.map((item, index) => (
                          <tr key={index}>
                            <td>{item.location || "N/A"}</td>
                            <td>{item.department || "N/A"}</td>
                            <td>{item.category || "-"}</td>
                            <td>{item.asset_count || 0}</td>
                            <td>{formatCurrency(item.total_value)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                <Pagination
                  currentPage={param.page}
                  recordsPage={param.page_size}
                  dataLength={totalCount}
                  handlePagination={handlePageChange}
                  showPageInfo
                  disabled={loading}
                />
              </>
            )}
          </Card.Body>
        </Card>
      </ErrorBoundary>
    </Container>
  );
};

export default AssetLocationDepartmentReport;
